import { useEffect, useState } from "react";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { Link } from "react-router-dom";
import { TopBar } from "./InventorySearch/TopBar";
import profilePicPlaceholder from "../assets/img/avatar-placeholder.svg";
import { TrashIcon } from "@heroicons/react/24/outline";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { SearchHistoryItem } from "../buyerStateDefs";
import {
  collapseSearchHistory,
  searchTargetUrl,
  searchText,
} from "./search-history-helpers";

export const SearchHistory = () => {
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [searchQuery] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<SearchHistoryItem[]>([]);

  useEffect(() => {
    if (!authUser) {
      setItems([]);
      return;
    }
    const ref = collection(db, `users/${authUser.uid}/user-search-history`);
    const unsub = onSnapshot(
      query(ref, orderBy("timestamp", "desc")),
      (snap) => {
        setItems(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as object) }) as SearchHistoryItem,
          ),
        );
      },
      (err) => console.error("search history subscription error", err),
    );
    return () => unsub();
  }, [authUser]);

  // Every document for that search on that day, or the row comes back as soon
  // as the snapshot catches up with the one copy we deleted.
  const deleteItems = async (ids: string[]) => {
    if (!authUser || ids.length === 0) return;
    try {
      await Promise.all(
        ids.map((id) =>
          deleteDoc(doc(db, `users/${authUser.uid}/user-search-history/${id}`)),
        ),
      );
    } catch (err) {
      console.error("Failed to delete search history item", err);
    }
  };

  const formatHistoryItems = () => {
    const searches = collapseSearchHistory(items, { perDay: true });
    let lastDateStr = "";
    const result = [];
    for (const search of searches) {
      const dateStr = search.at.toDateString();
      if (dateStr !== lastDateStr) {
        result.push(
          <div key={dateStr} className="font-bold mt-4 mb-2">
            {dateStr}
          </div>,
        );
        lastDateStr = dateStr;
      }
      result.push(
        <div
          key={search.key}
          className="group flex items-center justify-between text-gray-800 py-0.5"
        >
          <Link
            to={searchTargetUrl(search.item)}
            className="text-[#00062e] hover:text-[#009499]"
          >
            {searchText(search.item)}{" "}
            {search.hasFilters ? "(with filters)" : ""}
          </Link>
          <button
            onClick={() => deleteItems(search.ids)}
            className="ml-3 hidden shrink-0 text-gray-300 hover:text-red-500 group-hover:block"
            aria-label="Delete search"
            title={
              search.count > 1
                ? `Delete this search (${search.count} times that day)`
                : "Delete this search"
            }
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>,
      );
    }
    return result;
  };

  if (authIsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <TopBar
        fullScreen={false}
        searchQuery={searchQuery || ""}
        userProfileImg={profilePicPlaceholder}
      />
      <div className="pt-16 sm:pt-24">
        <main className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-2 mb-4">
            Past Searches
          </h2>
          <div>
            {items.length === 0 && <div>No searches yet</div>}
            {formatHistoryItems()}
          </div>
        </main>
      </div>
    </div>
  );
};
